import { MutableRefObject } from "react"
import { Plugin, PluginFactory, RenderInput } from "../../hugo/gpu"
import { Effect, Entity, GameField, GameState } from "./types"
import { getAt, getBottom, getLeft, getRight, getTop } from "./utils"
import levels from "./levels"
import { START_DELAY } from "./constants"
import { soundboard } from "../../hugo"


type PluginProps = {
    gameStateRef: { current: GameState }
}


/**
 * ┌──┬──┐  ╔══╦══╗ ╒══╤══╕ ╓──╥──╖
 │  │  │  ║  ║  ║ │  │  │ ║  ║  ║
 ├──┼──┤  ╠══╬══╣ ╞══╪══╡ ╟──╫──╢
 │  │  │  ║  ║  ║ │  │  │ ║  ║  ║
 └──┴──┘  ╚══╩══╝ ╘══╧══╛ ╙──╨──╜
 */
const msgPaused = 
`                                _ 
 ____   __ _ _   _ ___  ___  __| |
| '_ \\ / _' | | | / __|/ _ \\/ _' |
| |_) | (_| | |_| \\__ \\  __/ (_| |
| .__/ \\__,_|\\__,_|___/\\___|\\__,_|
|_|                               `

const msgThee = 
`         
 ┌─────┐ 
 │ __  │ 
 │  _) │ 
 │ __) │ 
 └─────┘ 
        `
const msgTwo = 
`         
 ┌─────┐ 
 │  __ │ 
 │   _)│ 
 │ /__ │ 
 └─────┘ 
         `
const msgOne = 
`         
 ┌─────┐ 
 │     │ 
 │ /|  │ 
 │  |  │ 
 └─────┘ 
         `

const useBreakoutGPUPlugin = ({ gameStateRef }: PluginProps): Plugin => {
    return (input, tick, resolution) => {
        const gameState = gameStateRef.current
         const newInput: RenderInput = input.map((row, y) => row.map((unit, x) => {
            switch (unit) {
                case Entity.Empty:
                    return ' '
                case Entity.BlockDefault:
                    if (x % 4 === 0) return ']'
                    if (x % 4 === 1) return '['
                    return '_'
                case Entity.BlockShield1:
                    if (x % 4 === 0) return ']'
                    if (x % 4 === 1) return '['
                    return '1'
                case Entity.BlockShield2:
                    if (x % 4 === 0) return ']'
                    if (x % 4 === 1) return '['
                    return '2'
                case Entity.BlockShield3:
                    if (x % 4 === 0) return ']'
                    if (x % 4 === 1) return '['
                    return '3'
                case Entity.BlockRandom:
                    if (x % 4 === 0) return ']'
                    if (x % 4 === 1) return '['
                    return '█'
                case Entity.Ball:
                    const ball = ['●']
                    if (gameState.effects.includes(Effect.PiercingBall)) {
                        ball.push('@')
                    }
                    return ball
                case Entity.Player:
                    const player = ['█', '█', '█']
                    if (
                        gameState.effects.includes(Effect.BallDuplicatorPlatform) &&
                        getLeft(x, y, input) === Entity.Player &&
                        getRight(x, y, input) === Entity.Player
                    ) {
                        player.push('▓')
                        player.push('▒')
                        player.push('░')
                        player.push('▒')
                        player.push('▓')
                    }
                    return player
                case Entity.Explosion:
                    return [' ', '*']
                case Entity.Buff:
                    return '?'
                case Entity.Debuff:
                    return '¿'

                case Entity.Wall:
                    // Blob tile calculation
                    let wang = 0;
                    // top
                    if (getTop(x, y, input) === Entity.Wall) wang += 1
                    // right
                    if (getRight(x, y, input) === Entity.Wall) wang += 2
                    // bttom
                    if (getBottom(x, y, input) === Entity.Wall) wang += 4
                    // left
                    if (getLeft(x, y, input) === Entity.Wall) wang += 8

                    //top-right
                    if (getAt(x + 1, y - 1, input) === Entity.Wall) wang += 16
                    // bottom right
                    if (getAt(x + 1, y + 1, input) === Entity.Wall) wang += 32
                    // bottom left
                    if (getAt(x - 1, y + 1, input) === Entity.Wall) wang += 64
                    // top left
                    if (getAt(x - 1, y - 1, input) === Entity.Wall) wang += 128

                    switch (wang) {
                        case 15:
                            return '╬'
                        case 94:
                        case 174:
                        case 46:
                            return '╦'
                        case 91:
                            return '╩'
                        case 11:
                        case 14:
                        case 142:
                        case 158:
                        case 96:
                        case 100:
                        case 110:
                        case 155:
                        case 187:
                        case 219:
                        case 34:
                        case 108:
                        case 171:
                        case 42:
                        case 74:
                        case 129:
                        case 138:
                        case 17:
                        case 202:
                        case 26:
                            return '═'
                        case 9:
                        case 137:
                        case (255 - 128):
                            return '╝'
                        case 12:
                        case 76:
                        case (255 - 64):
                            return '╗'
                        case 3:
                        case 19:
                        case 147:
                        case (255 - 16):
                            return '╚'
                        case 6:
                        case 38:
                        case 102:
                        case (255 - 32):
                            return '╔'
                        case 1:
                        case 4:
                        case 5:
                        case 13:
                        case 7:
                        case 221:
                        case 55:
                        case 21:
                        case 133:
                        case 183:
                        case 69:
                        case 37:
                        case 149:
                            return '║'
                        case 10:
                        case 2:
                        case 8:
                            return '═'
                        case 0:
                            return '═'
                        default:
                            return '═'
                    }
                default:
                    return ' '
            }
        }))

        const renderNotes: string[] = []
        gameState.notifications.forEach(notification => {
            if (tick > notification.startTick && tick < notification.endTick) {
                renderNotes.push(notification.message)
            }
        })

        renderNotes.forEach((note, y) => {
            const relativeY = y + 6;
            const startX = 17 - Math.round(note.length / 2)
            note.split('').forEach((character, xOffset) => {
                if (newInput[relativeY] && typeof newInput[relativeY][startX + xOffset] !== "undefined") {
                    newInput[relativeY][startX + xOffset] = character
                }
            })
        })

        if (gameState.currentLevel !== null && levels[gameState.currentLevel]) {
            const name = '[' + levels[gameState.currentLevel].name + ']'
            const startX = 17 - Math.round(name.length / 2)
            name.split('').forEach((character, xOffset) => {
                if (newInput[0] && typeof newInput[0][startX + xOffset] !== "undefined") {
                    newInput[0][startX + xOffset] = character
                }
            })
        }


        if (!gameState.isPlaying) {

            const startX = 0
            msgPaused.split('\n').forEach((row, yOffset) => {
                row.split('').forEach((character, xOffset) => {
                    if (character === ' ') return
                    if (newInput[4 + yOffset] && typeof newInput[4 + yOffset][xOffset + startX] !== "undefined") {
                        newInput[4 + yOffset][xOffset + startX] = character
                    }
                })
            })
        }


        if (gameState.tick < START_DELAY && gameState.isPlaying) {
            const startX = 12
            const startY = 4

            if (gameState.tick === 1) soundboard.typewrite1()
            if (gameState.tick === Math.ceil(START_DELAY / 3)) soundboard.typewrite1()
            if (gameState.tick === Math.ceil(START_DELAY / 3 * 2)) soundboard.typewrite1()
                
            if (gameState.tick < START_DELAY / 3) {
                msgThee.split('\n').forEach((row, yOffset) => {
                    row.split('').forEach((character, xOffset) => {
                        if (newInput[startY + yOffset] && typeof newInput[startY + yOffset][xOffset + startX] !== "undefined") {
                            newInput[startY + yOffset][xOffset + startX] = character
                        }
                    })
                })
            } else if (gameState.tick < (START_DELAY / 3 * 2)) {
                msgTwo.split('\n').forEach((row, yOffset) => {
                    row.split('').forEach((character, xOffset) => {
                        if (newInput[startY + yOffset] && typeof newInput[startY + yOffset][xOffset + startX] !== "undefined") {
                            newInput[startY + yOffset][xOffset + startX] = character
                        }
                    })
                })
            } else if (gameState.tick < (START_DELAY)) {
                msgOne.split('\n').forEach((row, yOffset) => {
                    row.split('').forEach((character, xOffset) => {
                        if (newInput[startY + yOffset] && typeof newInput[startY + yOffset][xOffset + startX] !== "undefined") {
                            newInput[startY + yOffset][xOffset + startX] = character
                        }
                    })
                })
            }
        }

        return newInput
    }
}

export default useBreakoutGPUPlugin
